import Colour from 'color';
import humanFormat from 'human-format';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import useDimensions from 'react-use-dimensions';
import ReactTooltip from 'react-tooltip';
import theme from '../../../assets/stylesheets/_settings.global.scss';
import { useIsMobile } from '../../shared/theme';
import { recordStatisticsEvent } from '../../services/statistics-service';

const Container = styled.div`
  .tooltip {
    opacity: 1 !important;
    border: 1px solid #D7DFE7;
    border-radius: 6px;
    box-shadow: 0 0 9px 5px rgba(53, 65, 76, 0.2);
    padding: 0 !important;

    &::after {
      border-left: 16px solid transparent !important;
      border-right: 16px solid transparent !important;
      bottom: -12px !important;
      margin-left: -16px !important;
      border-top-width: 12px !important;
    }
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0 12px;
  width: 308px;
`;

const CompanyContainer = styled.div`
  height: 48px;
  padding: 13px 0 13px 16px;
  background-color: ${props => props.bgColor};
  border: 1px solid ${props => props.bgColor};
  border-radius: 6px 6px 0 0;
`;

const Divider = styled.div`
  height: 0px;
  border-top: 1px solid #E6EBEF;
  width: 100%;
`;

const CompanyLogo = styled.img`
  height: 22px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 16px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0 0 16px;
  overflow-x: hidden;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeightHeavy};
  color: ${props => props.theme.bodyColor};
  line-height: 120%;
  text-transform: uppercase;
  margin-bottom: 1px;
`;

const CompanyName = styled.div`
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeightBold};
  color: ${props => props.theme.secondaryColor};
  line-height: 110%;
  width: 100%;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1.36px solid white;
  filter: drop-shadow(0px 2.74px 5.48px rgba(50, 50, 71, 0.2));
`;

const Separator = styled.div`
  height: 0px;
  border-top: 1px solid #D7DFE7;
  margin: 16px 0 12px;
  width: 100%;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 0 16px;
`;

const Follower = styled.div`
  font-size: 15px;
  font-weight: ${props => props.theme.fontWeightBold};
  color: ${props => props.theme.secondaryColor};
  line-height: 120%;
  margin-top: 6px;

  span.number {
    color: ${props => props.theme.bodyColor};
  }
`;

const Button = styled.button`
  width: 98px;
  height: 32px;
  font-size: 12px;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  line-height: 120%;
  text-transform: uppercase;
  color: ${props => props.theme.bodyBackgroundColor};
  background-color: #3D4A63;
  border: 1px solid #3D4A63;
  border-radius: 2px;
  outline: none;
`;

const white = Colour('white').hex();

export default function ContributorTooltip({ contributor }) {
  const [showCompanyName, setShowCompanyName] = React.useState(true);

  const [nameRef, { height }] = useDimensions();

  React.useLayoutEffect(() => setShowCompanyName(height <= 20), [height]);

  const {
    id,
    name,
    profilePictureUrl,
    publicUrl,
    followersCount,
    company: {
      name: companyName,
      logo: companyLogo,
      color: companyColor,
    },
  } = contributor;

  const tooltipId = `Contributor-${id}`;

  let companyBackgroundColor;
  try {
    companyBackgroundColor = companyColor ? Colour(companyColor).hex() : white;
  } catch (err) {
    companyBackgroundColor = white;
  }

  const handleOnClick = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'contributor_mention_popup_click',
      statisticable: 'profile',
      id,
      clicked_url: publicUrl,
      page: window.location.pathname,
    });

    window.location = publicUrl;
  };

  const onShow = () => {
    recordStatisticsEvent({
      event: 'contributor_mention_popup_hover',
      statisticable: 'profile',
      id,
      page: window.location.pathname,
    });
  };

  const isMobile = useIsMobile();

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <ReactTooltip
          id={tooltipId}
          type="light"
          effect="solid"
          delayHide={200}
          clickable
          className="tooltip"
          afterShow={onShow}
          disable={isMobile}
        >
          {companyLogo && (
            <>
              <CompanyContainer bgColor={companyBackgroundColor}>
                <CompanyLogo src={companyLogo} alt={companyName} />
              </CompanyContainer>
              {companyBackgroundColor === white && <Divider />}
            </>
          )}
          <ProfileContainer>
            <HeaderContainer>
              <Avatar
                src={profilePictureUrl || window.getLivewireAssetPath('avatar-fallback.png')}
                alt={name}
              />
              <InfoContainer>
                <Name ref={nameRef}>{name}</Name>
                {showCompanyName && <CompanyName>{companyName}</CompanyName>}
              </InfoContainer>
            </HeaderContainer>
            <Separator />
            <FooterContainer>
              <Button onClick={handleOnClick}>View Profile</Button>
              {followersCount > 0 && (
                <Follower>
                  Followed by
                  <span className="number">
                    {` ${humanFormat(followersCount, { decimals: 1, separator: '' })} `}
                  </span>
                  people
                </Follower>
              )}
            </FooterContainer>
          </ProfileContainer>
        </ReactTooltip>
      </Container>
    </ThemeProvider>
  );
}

ContributorTooltip.propTypes = {
  contributor: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    profilePictureUrl: PropTypes.string,
    publicUrl: PropTypes.string.isRequired,
    followersCount: PropTypes.number.isRequired,
    company: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
      color: PropTypes.string,
    }),
  }).isRequired,
};
